import * as React from 'react'
import { createSearchParams, useNavigate } from 'react-router-dom'

import { Box, Button, Text, Tooltip, useToggle } from '@factorialco/deprecated-design-system'

import { useNavigation } from 'modules/core/hooks/useNavigation'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'

import IncidenceInformation from 'modules/bookkeepers/components/IncidenceInformation'
import { IncidencesProvider } from 'modules/bookkeepers/hooks/IncidencesContext'
import useGetIncidences from 'modules/bookkeepers/hooks/useGetIncidences'
import type { DefaultParams } from 'modules/bookkeepers/hooks/useIncidenceFilters'
import useIncidenceTypeQueryParam from 'modules/bookkeepers/hooks/useIncidenceTypeQueryParam'
import { useIncidenceFilterContext } from 'modules/bookkeepers/lib/IncidenceFilterContext'
import { MessageFilterProvider } from 'modules/bookkeepers/lib/MessageFilterContext'
import { PROFESSIONALS_APP } from 'modules/bookkeepers/lib/amplitudeData'
import {
  incidenceTypeNavigation,
  translatedTitle,
} from 'modules/bookkeepers/lib/incidenceTypeNavigation'
import { SORT_ATTRIBUTES } from 'modules/bookkeepers/lib/tableSortColumns'
import { useHasCompensations } from 'modules/payroll_bundle/hooks/useHasCompensations'
import { DATA_VIEW } from 'modules/payroll_bundle/lib/Incidences/Status'
import {
  PolicyPeriodsProvider,
  usePolicyPeriods,
} from 'modules/payroll_bundle/lib/policyPeriodsContext'
import { PolicyPeriods } from 'modules/payroll_bundle/store/collections/policyPeriods'
import PayrollExportModal from 'modules/payroll_exporter/components/PayrollExportModal'
import OauthBanner from 'modules/payroll_integrations_a3innuva/components/OauthBanner'

import { useDownloadIncidencesExport } from '../../lib/useDownloadIncidencesExport'
import DetailSidepanel from './DetailSidepanel'
import IncidencesTable from './IncidencesTable'

interface Props {
  incidenceDetail: React.ReactNode
  selectedIncidenceId: number | null | undefined
}

const Content = ({ incidenceDetail, selectedIncidenceId }: Props) => {
  const navigate = useNavigate()
  const { navigation } = useNavigation()
  const incidencesFilterData = useIncidenceFilterContext()
  const { incidenceType } = useIncidenceTypeQueryParam()
  const { loading, incidences } = useGetIncidences()
  const policyPeriodsContext = usePolicyPeriods()
  const policyPeriodCollection = new PolicyPeriods()
  const { hasCompensations: hasCompensationsModule } = useHasCompensations()
  const { onSubmit } = useDownloadIncidencesExport(
    incidencesFilterData,
    DATA_VIEW.bookkeeper,
    incidenceType
  )
  const incidenceTypeParams = createSearchParams({ incidenceType })

  const closeSidePanel = () => {
    const incidencesRoute = !!incidenceType
      ? `${navigation.bookkeepers.incidences.fullPath()}?${incidenceTypeParams}`
      : navigation.bookkeepers.incidences.fullPath()

    navigate(incidencesRoute)
  }

  const [exportModalOpen, toggleExportModalOpen] = React.useState(false)
  const [showSourceTooltip, toggleSourceTooltip] = useToggle(false)

  const hasExport = !incidenceType || incidenceType === incidenceTypeNavigation.ALL
  const hasNewHiresExport = incidenceTypeNavigation.NEW_HIRES === incidenceType

  const content = (
    <PayrollExportModal
      onClose={() => toggleExportModalOpen(false)}
      selectedCompany={incidencesFilterData?.selectedCompany?.id}
      isProfessionals
    />
  )

  return (
    <>
      <IncidencesProvider value={incidences}>
        <Box gap="s6" flexDirection="row" width="full" justifyContent="spaceBetween">
          <Text.Normal weight="bold">{translatedTitle(incidenceType)}</Text.Normal>
          {hasExport && !incidencesFilterData.selectedCompany && (
            <Tooltip.Alt
              open={showSourceTooltip}
              toggle={toggleSourceTooltip}
              label={i18n.t('bookkeepers.header.select_company_to_enable_export')}
              attachment="middle right"
              size="big"
              inline={false}
            >
              <Button disabled type="secondary" iconName="download">
                {i18n.t('bookkeepers.header.export_data')}
              </Button>
            </Tooltip.Alt>
          )}
          {hasExport && !!incidencesFilterData.selectedCompany && (
            <Button
              onClick={() => {
                if (hasCompensationsModule) {
                  track(trackingEvents.compensations.openEmployeeUpdatesExportModal, {
                    application: PROFESSIONALS_APP,
                  })
                }
                toggleExportModalOpen(true)
              }}
              type="secondary"
              iconName="download"
            >
              {i18n.t('bookkeepers.header.export_data')}
            </Button>
          )}
          {hasNewHiresExport && (
            <Button onClick={() => onSubmit()} type="secondary" iconName="download">
              {i18n.t('bookkeepers.header.export_new_hires')}
            </Button>
          )}
        </Box>

        <OauthBanner />
        <Box gap="s0" padding="s0" flexDirection="row" width="full" alignItems="flexStart">
          <IncidencesTable loading={loading} selectedIncidence={selectedIncidenceId} />
          <DetailSidepanel closeSidePanel={closeSidePanel} open={!loading && !!selectedIncidenceId}>
            <IncidenceInformation incidenceDetail={incidenceDetail} />
          </DetailSidepanel>
        </Box>
      </IncidencesProvider>
      {exportModalOpen && policyPeriodsContext.policyPeriodCollection && content}
      {exportModalOpen && !policyPeriodsContext.policyPeriodCollection && (
        <PolicyPeriodsProvider policyPeriodCollection={policyPeriodCollection} isBookkeeper>
          {content}
        </PolicyPeriodsProvider>
      )}
    </>
  )
}

const IncidenceTableWithFilters = ({ incidenceDetail, selectedIncidenceId }: Props) => {
  const defaultMessagesFilterValues: DefaultParams = {
    direction: 'desc',
    attribute: SORT_ATTRIBUTES.incidence_effective_date,
  }

  return (
    <MessageFilterProvider defaultFilterValues={defaultMessagesFilterValues}>
      <Content incidenceDetail={incidenceDetail} selectedIncidenceId={selectedIncidenceId} />
    </MessageFilterProvider>
  )
}

export default IncidenceTableWithFilters
